/**
 * IONLOOP Assistant — deterministic domain concept layer.
 *
 * Canonical concepts separate from raw words. Bridges user vocabulary to the
 * indexed IONLOOP terminology without an LLM. Alias weights are differentiated:
 *   1.00 exact page terminology
 *   0.95 direct morphological variant
 *   0.85 strong alias
 *   0.70 synonym
 *   0.40 weak association
 *
 * Pre-built lookup maps make alias resolution near constant-time.
 *
 * (English first. The architecture supports adding a Latvian concept set by
 *  supplying an alternative CONCEPTS table — this project is English.)
 */

// ---------------------------------------------------------------------------
// Canonical concepts
// ---------------------------------------------------------------------------
const CONCEPTS = {
    pricing: {
        aliases: {
            pricing: 1.0, price: 0.95, prices: 0.95, priced: 0.9, cost: 0.85, costs: 0.85,
            fee: 0.7, fees: 0.7, rates: 0.6, tier: 0.7, tiers: 0.7, paid: 0.55, plans: 0.8, plan: 0.55,
            'how much': 0.9, 'how much is it': 0.9, 'how much does it cost': 0.95,
            'how much does ionloop cost': 0.95, 'what does it cost': 0.9,
            'what does ionloop cost': 0.9, 'what are the prices': 0.9,
            'how much do plans cost': 0.9, 'subscription cost': 0.85,
            'subscription price': 0.85, 'plan price': 0.85, 'account levels': 0.85,
            'is it free': 0.75, 'is ionloop free': 0.85, 'what plans are available': 0.85,
            'what are the fees': 0.85, 'how much is': 0.8, 'how much are': 0.8
        },
        related: ['subscription', 'account']
    },
    subscription: {
        aliases: { subscription: 1.0, subscribe: 0.9, subscribed: 0.85, subscriptions: 0.95, 'subscription fee': 0.85 },
        related: ['pricing']
    },
    'getting-started': {
        aliases: { start: 0.85, starts: 0.8, started: 0.9, 'getting started': 1.0, 'get started': 0.95, onboarding: 0.85, 'first steps': 0.85, 'where do i begin': 0.85, 'how do i start': 0.9, 'create account': 0.8, 'open account': 0.8, 'sign up': 0.7, begin: 0.7 },
        related: ['account']
    },
    'ionloop-id': {
        aliases: { 'ionloop id': 1.0, identity: 0.7, 'single sign on': 0.85, 'single sign-on': 0.85, 'account identity': 0.85 },
        related: ['account']
    },
    account: {
        aliases: { account: 1.0, accounts: 0.95, profile: 0.7, settings: 0.6, 'my account': 0.9, login: 0.6, 'log in': 0.6, 'sign in': 0.6, id: 0.3 },
        related: ['account-security', 'account-recovery', 'ionloop-id']
    },
    'account-security': {
        aliases: {
            security: 1.0, secure: 0.95, secured: 0.9, safety: 0.7, safe: 0.65, protect: 0.75, protected: 0.8, protection: 0.75,
            'is my account safe': 0.9, 'how is account protected': 0.9, 'is it secure': 0.85, '2fa': 0.8, 'two factor': 0.8, 'two-factor': 0.8,
            password: 0.7, passwords: 0.7, device: 0.6, devices: 0.6, encryption: 0.8, 'security tools': 0.85
        },
        related: ['account', 'account-recovery']
    },
    'account-recovery': {
        aliases: {
            'forgot password': 1.0, 'lost password': 0.95, "can't login": 0.85, "cannot login": 0.85, "can't sign in": 0.85,
            "can't log in": 0.85, 'recover account': 0.95, 'account recovery': 0.95, 'recover my account': 0.95, recover: 0.7,
            'reset password': 0.85, 'lost access': 0.8, "can't access my account": 0.9, 'restore password': 0.9, restore: 0.6
        },
        related: ['account-security', 'account']
    },
    trading: {
        aliases: {
            trading: 1.0, trade: 0.95, trades: 0.95, traded: 0.9, trader: 0.8, markets: 0.75, market: 0.6,
            'trading tools': 0.9, 'trade panel': 0.85, 'trading panel': 0.85, 'trade dashboard': 0.85,
            orders: 0.7, order: 0.65, chart: 0.5, 'trading fees': 0.9, 'trade fees': 0.85
        },
        related: ['spot', 'margin', 'futures', 'market-data']
    },
    spot: { aliases: { spot: 1.0, 'spot trading': 1.0, 'spot market': 0.9 }, related: ['trading'] },
    margin: { aliases: { margin: 1.0, 'margin trading': 1.0 }, related: ['trading'] },
    futures: { aliases: { futures: 1.0, 'futures trading': 1.0 }, related: ['trading'] },
    'market-data': {
        aliases: { 'btc price': 1.0, 'eth price': 1.0, 'bitcoin price': 1.0, 'asset price': 0.9, 'market price': 0.9, 'coin price': 0.8, 'prices of assets': 0.8 },
        related: ['trading']
    },
    wallet: {
        aliases: { wallet: 1.0, wallets: 0.95, 'crypto wallet': 1.0, money: 0.45, funds: 0.5, balance: 0.55, balances: 0.6, 'wallet balance': 0.85 },
        related: ['payments']
    },
    payments: {
        aliases: { payment: 1.0, payments: 1.0, paying: 0.9, pay: 0.85, paid: 0.6, transfer: 0.65, transfers: 0.7, deposit: 0.65, deposits: 0.65, withdraw: 0.65, withdrawal: 0.65 },
        related: ['wallet', 'automation']
    },
    automation: {
        aliases: { automation: 1.0, automate: 0.95, automated: 0.95, automating: 0.9, autopay: 0.85, 'auto pay': 0.95, workflow: 0.7, workflows: 0.7, recurring: 0.6, 'recurring payments': 0.8, 'automatic payments': 0.9 },
        related: ['payments']
    },
    planning: {
        aliases: { planning: 1.0, plan: 0.6, plans: 0.55, planner: 0.8, schedule: 0.55, scheduling: 0.6, 'organize work': 0.8, 'planning tools': 0.9, 'planning tool': 0.85 },
        related: ['calendar', 'sheets', 'diagrams']
    },
    calendar: { aliases: { calendar: 1.0, calendars: 0.95, 'planner calendar': 0.85, reminder: 0.55, reminders: 0.55, 'calendar builder': 1.0 }, related: ['planning'] },
    sheets: { aliases: { sheet: 0.95, sheets: 0.95, spreadsheet: 1.0, spreadsheets: 1.0, tabular: 0.6, 'sheet editor': 1.0 }, related: ['planning'] },
    diagrams: { aliases: { diagram: 1.0, diagrams: 1.0, flowchart: 0.85, 'diagram builder': 1.0 }, related: ['planning'] },
    'business-operations': {
        aliases: { business: 0.6, 'business tools': 0.85, operations: 0.65, 'manage company': 0.8, 'manage business': 0.8, 'business management': 0.85, erp: 0.85, company: 0.45, 'company tools': 0.8 },
        related: ['planning', 'payments']
    },
    help: { aliases: { help: 0.8, support: 0.7, faq: 0.85, 'help center': 1.0, 'help me': 0.8 }, related: ['contact'] },
    policies: { aliases: { policy: 1.0, policies: 1.0, privacy: 0.9, terms: 0.85, cookie: 0.85, cookies: 0.85, compliance: 0.7 }, related: ['help'] },
    downloads: { aliases: { download: 1.0, downloads: 1.0, 'desktop app': 0.85, 'mobile app': 0.8, client: 0.6 }, related: ['getting-started'] },
    contact: { aliases: { contact: 1.0, contacts: 0.95, reach: 0.7, 'support team': 0.8, 'help desk': 0.8, 'customer support': 0.8, 'talk to someone': 0.8 }, related: ['help'] },
    overview: {
        aliases: { features: 0.7, feature: 0.6, tools: 0.6, 'what does ionloop do': 0.95, 'what can i use ionloop for': 0.95, 'what can i do with ionloop': 0.95, 'what is available': 0.8, 'what can it do': 0.85, 'what does it do': 0.8, 'what features are there': 0.9, capabilities: 0.8, 'what does ionloop offer': 0.9 },
        related: []
    }
};

// ---------------------------------------------------------------------------
// Product entities (preserved as units, not individual tokens)
// ---------------------------------------------------------------------------
const ENTITIES = {
    'ionloop id': 'ionloop-id',
    'auto pay': 'automation',
    autopay: 'automation',
    'signal explorer': 'trading',
    'cycle constructor': 'trading',
    'trading workspace': 'trading',
    'trade panel': 'trading',
    'trading panel': 'trading',
    'trade dashboard': 'trading',
    'trading dashboard': 'trading',
    'account levels': 'pricing',
    'sheet editor': 'planning',
    'diagram builder': 'planning',
    'calendar builder': 'planning',
    'appointment builder': 'planning',
    'notification system': 'automation',
    'crypto wallet': 'wallet',
    'p2p file sharing': 'business-operations',
    'file sharing': 'business-operations',
    'ionloop security': 'account-security',
    'time tracker': 'planning',
    'planner constructor': 'trading',
    'campaign builder': 'trading',
    'edge constructor': 'trading',
    convert: 'trading'
};

// ---------------------------------------------------------------------------
// Contextual disambiguation rules (price, fee, etc.)
// ---------------------------------------------------------------------------
const CONTEXT_RULES = [
    // price + crypto/asset → market-data (and suppress subscription pricing)
    { concept: 'market-data', trigger: ['price', 'prices', 'cost', 'value'], context: ['btc', 'eth', 'bitcoin', 'asset', 'market', 'coin', 'crypto'], suppress: ['pricing'] },
    // price/fee + plan/subscription → pricing
    { concept: 'pricing', trigger: ['price', 'prices', 'fee', 'fees', 'cost'], context: ['plan', 'plans', 'subscription', 'account level', 'tier', 'tiers', 'year', 'month'], suppress: ['market-data'] },
    // fee + trading/order → trading
    { concept: 'trading', trigger: ['fee', 'fees', 'cost', 'price'], context: ['trading', 'order', 'orders', 'spot', 'margin', 'futures', 'market', 'trade'], suppress: ['pricing'] },
    // free → pricing
    { concept: 'pricing', trigger: ['free', 'paid'], context: ['ionloop', 'plan', 'plans', 'subscription', 'account'] }
];

// ---------------------------------------------------------------------------
// Pre-built lookup maps (near constant-time)
// ---------------------------------------------------------------------------
let _aliasToConcept = null;
let _conceptAliases = null;

function buildMaps() {
    if (_aliasToConcept) return;
    _aliasToConcept = new Map();
    _conceptAliases = {};
    Object.keys(CONCEPTS).forEach((conceptId) => {
        const concept = CONCEPTS[conceptId];
        _conceptAliases[conceptId] = [];
        Object.keys(concept.aliases).forEach((alias) => {
            const key = alias.toLowerCase().trim();
            // A single alias may belong to multiple concepts (e.g. "plan" →
            // pricing AND planning). Store an array so no concept is dropped.
            if (!_aliasToConcept.has(key)) {
                _aliasToConcept.set(key, []);
            }
            _aliasToConcept.get(key).push({ conceptId, weight: concept.aliases[alias] });
            _conceptAliases[conceptId].push({ term: key, weight: concept.aliases[alias] });
        });
    });
}

function aliasToConceptMap() {
    buildMaps();
    return _aliasToConcept;
}

function conceptAliases(conceptId) {
    buildMaps();
    return _conceptAliases[conceptId] || [];
}

/**
 * Detect the concept(s) expressed by a normalized query.
 * @param {string} normalized lowercased, normalized query text
 * @param {Array<string>} meaningfulTokens
 * @returns {Array<{id, confidence, terms:Array<{term,weight,kind}>}>}
 */
function detectConcepts(normalized, meaningfulTokens) {
    const map = aliasToConceptMap();
    const found = {}; // conceptId -> {confidence, terms:Map}

    // 1) Phrase-level (multi-word) matches against the full normalized query.
    map.forEach((entries, alias) => {
        if (alias.indexOf(' ') === -1) return; // phrases only in this pass
        if (normalized.indexOf(alias) !== -1) {
            entries.forEach((entry) => addConcept(found, entry.conceptId, entry.weight, alias, 'phrase'));
        }
    });

    // 2) Single-token matches (a token may map to several concepts).
    meaningfulTokens.forEach((tok) => {
        const entries = map.get(tok);
        if (entries) {
            entries.forEach((entry) => addConcept(found, entry.conceptId, entry.weight, tok, 'token'));
        }
    });

    // 3) Contextual disambiguation: refine weights and suppress competing
    //    concepts (e.g. BTC price → market-data, not subscription pricing).
    applyContextRules(found, normalized, meaningfulTokens);

    return Object.keys(found).map((id) => ({
        id,
        confidence: found[id].confidence,
        terms: Array.from(found[id].terms.values())
    }));
}

function addConcept(found, conceptId, weight, term, kind) {
    if (!found[conceptId]) {
        found[conceptId] = { confidence: 0, terms: new Map() };
    }
    found[conceptId].confidence = Math.max(found[conceptId].confidence, weight);
    if (!found[conceptId].terms.has(term)) {
        found[conceptId].terms.set(term, { term, weight, kind });
    }
}

function applyContextRules(found, normalized, meaningfulTokens) {
    CONTEXT_RULES.forEach((rule) => {
        const hasTrigger = rule.trigger.some((t) => normalized.indexOf(t) !== -1);
        if (!hasTrigger) return;
        const hasContext = rule.context.some((c) => normalized.indexOf(c) !== -1);
        if (!hasContext) return;
        // Boost the rule's target concept.
        addConcept(found, rule.concept, 0.95, 'context', 'contextual');
        found[rule.concept].confidence = Math.max(found[rule.concept].confidence, 0.95);
        // Suppress competing concepts strongly signalled by the context.
        (rule.suppress || []).forEach((other) => {
            if (found[other]) {
                found[other].confidence = Math.min(found[other].confidence, 0.3);
            }
        });
    });
}

/**
 * Expand a concept into search terms (limited, strong relationships only).
 * @param {string} conceptId
 * @returns {Array<{term, weight}>}
 */
function expandConcept(conceptId) {
    const aliases = conceptAliases(conceptId);
    const out = aliases.map((a) => ({ term: a.term, weight: a.weight }));
    // Include related concepts at reduced weight.
    const concept = CONCEPTS[conceptId];
    (concept.related || []).forEach((relId) => {
        const rel = CONCEPTS[relId];
        if (rel) {
            Object.keys(rel.aliases).forEach((alias) => {
                out.push({ term: alias.toLowerCase().trim(), weight: Math.min(0.5, rel.aliases[alias] * 0.5) });
            });
        }
    });
    // Dedupe.
    const seen = new Set();
    return out.filter((o) => {
        if (seen.has(o.term)) return false;
        seen.add(o.term);
        return true;
    }).slice(0, 20);
}

/**
 * Detect product entities in a query. Multi-word entities matched first.
 * @returns {Array<string>} entity keys (canonical product names)
 */
function detectEntities(normalized) {
    const out = [];
    Object.keys(ENTITIES).forEach((entity) => {
        if (normalized.indexOf(entity) !== -1) {
            out.push(entity);
        }
    });
    return out;
}

/**
 * Return the canonical concept id for a single token if it is a strong
 * unambiguous alias (used by the one-word topic-discovery path).
 */
function strongConceptForToken(token) {
    const map = aliasToConceptMap();
    const entry = map.get(token);
    if (entry && entry.weight >= 0.85) {
        return entry.conceptId;
    }
    return null;
}

module.exports = {
    CONCEPTS,
    ENTITIES,
    detectConcepts,
    detectEntities,
    expandConcept,
    strongConceptForToken,
    aliasToConceptMap
};
